import { createSelector } from 'reselect'
import { createGetter, composeGetters } from 'react-redux-getters'
import { getCollection, replaceCollections } from '@fakundo/redux-entities'
import reject from 'lodash/reject'
import find from 'lodash/find'
import keyBy from 'lodash/keyBy'
import some from 'lodash/some'
import orderBy from 'lodash/orderBy'
import map from 'lodash/map'
import moment from 'moment'
import { getProposalField } from 'utils/proposals'
import { fetchProposals } from 'actions/proposals'
import { PROPOSALS } from 'constants/entityNames'
import { LIVE_CHAT, INSTANT_NEWSLETTER_SUBSCRIBE, MINDBODY } from 'constants/proposalFieldTypes'
import { isMailChimpConnected, isMindbodyConnected } from 'selectors/integrations'

export const getProposals = createGetter({
  stateSelector: state => getCollection(state, PROPOSALS),
  asyncFetcher: dispatch => dispatch(fetchProposals()),
  stateUpdater: (proposals, dispatch) => dispatch(replaceCollections({
    [PROPOSALS]: keyBy(proposals, 'uuid')
  })),
})

export const makeGetProposal = () => createSelector(
  state => getProposals(state),
  (state, { proposalId, proposalUuid }) => proposalId || proposalUuid,
  (proposalsGetter, proposalId) => composeGetters(
    proposalsGetter,
    (proposals) => {
      const proposal = find(proposals, ({ uuid, id }) => {
        return uuid === proposalId || id === proposalId
      })
      if (!proposal) throw new Error('Proposal not found')
      return proposal
    }
  )
)

export const getOrderedFilteredProposals = createSelector(
  getProposals,
  isMailChimpConnected,
  isMindbodyConnected,
  (proposalsGetter, isMailChimpConnectedGetter, isMindbodyConnectedGettter) => composeGetters(
    proposalsGetter,
    isMailChimpConnectedGetter,
    isMindbodyConnectedGettter,
    (proposals, mailChimpConnected, mindbodyConnected) => {
      const filteredProposals = reject(proposals, { deleted: true })
      const orderedProposals = orderBy(filteredProposals, ['order', 'id'], ['asc', 'desc'])
      return map(orderedProposals, (proposal) => {
        if (
          (!mindbodyConnected && getProposalField(proposal, MINDBODY))
          || (!mailChimpConnected && getProposalField(proposal, INSTANT_NEWSLETTER_SUBSCRIBE))
        ) {
          return { ...proposal, data: { ...proposal.data, disabledInstantly: true } }
        }
        return proposal
      })
    }
  )
)

export const getLiveChatOfferProposals = createSelector(
  getOrderedFilteredProposals,
  proposalsGetter => composeGetters(
    proposalsGetter,
    (proposals) => {
      return reject(proposals, proposal => (
        // Omit past events
        (proposal.endsAt && moment().isAfter(proposal.endsAt))
        // Omit instantly disabled proposals
        || (proposal.data && proposal.data.disabledInstantly)
        // Omit live chats
        || (proposal.data && some(proposal.data.fields, { type: LIVE_CHAT }))
      ))
    }
  )
)
